import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { Box, Flex, xcss, Text, Inline, Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import EditorInfoIcon from '@atlaskit/icon/glyph/editor/info';
import { ExtendedInvoicePPC, InvoiceWithLineItemsPPC } from '@css-xp/billing-v2';
import { EndCustomerContacts } from '@css-xp/contact-resources';
import { NoData, BillingProfileNameFromId, BillingProfileNumberFromId } from '@css-xp/ppc-common';
import { Maybe, Scalars, TransactionAccount } from '@css-xp/graphtypes/src/generated-types-new';
import { useTransactionAccountContext } from '@css-xp/base/common-context';
import { ContactsTooltip, ContactsTooltipType, CopyableText } from '@css-xp/base';
import {
  getSoldToAddress,
  getPaymentMethodSection,
} from '../invoice-details-summary-section/invoice-details-summary-section';

const boxStyles = xcss({
  backgroundColor: 'elevation.surface',
  borderColor: 'color.border',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderRadius: 'border.radius.200',
});

const currencyBoxStyles = xcss({
  paddingTop: 'space.200',
});

const CurrencyCodeText = styled.span`
  color: ${token('color.text')};
  margin-bottom: ${token('space.100')};
`;

const infoStyles = xcss({
  marginTop: 'space.400',
});
const billingProfileStyles = xcss({
  display: 'grid',
  gap: `space.1000`,
  gridTemplateColumns: '0.145fr 0.25fr 0.2fr',
});
const LabelWrapper = styled.span`
  font: ${token('font.heading.xxsmall')};
  color: ${token('color.text.subtlest')};
  font-weight: ${token('font.weight.semibold')};
  margin-bottom: ${token('space.100')};
`;

type ExtendedInvoice = ExtendedInvoicePPC & {
  totalCreditLimit?: Maybe<Scalars['Float']>;
  onTermPaymentMethod?: Maybe<Scalars['String']>;
};

interface Props {
  invoice: InvoiceWithLineItemsPPC;
  hasPayByStatement?: boolean;
  invoiceGroupId?: string;
  transactionAccountId?: string;
  isPaypalEnabled?: boolean;
  isMultiCurrencyChangesEnabled?: boolean;
}

export const compareContactTimestampWithInvoiceTimestamp = (invoiceCreatedTimestamp?: string | null) => {
  return (contactTimestamp: Maybe<string> | undefined) => {
    return moment.utc(contactTimestamp).isSameOrBefore(moment.utc(invoiceCreatedTimestamp));
  };
};

export const BillingProfileDetailsSection = ({
  invoice,
  hasPayByStatement,
  invoiceGroupId,
  transactionAccountId,
  isPaypalEnabled = false,
  isMultiCurrencyChangesEnabled,
}: Props) => {
  const { onTermPaymentMethod } = invoice as ExtendedInvoice;
  const { data: transactionAccountData, getTransactionAccount } = useTransactionAccountContext();
  const transactionAccount = getTransactionAccount(transactionAccountData) as TransactionAccount;

  const currencyCode = isMultiCurrencyChangesEnabled ? invoice?.pricingCurrency : invoice?.paymentCurrency;
  const termPaymentStatus = useMemo(() => {
    const termPaymentStatus = transactionAccount?.billingAccountsList?.status;

    return termPaymentStatus;
  }, [transactionAccount]);

  const billingProfileName = useMemo(() => {
    return (
      <BillingProfileNameFromId id={invoiceGroupId} txaId={transactionAccountId || ''} skeletonLoaderWidth="100px" />
    );
  }, [invoiceGroupId, transactionAccountId]);

  const billingProfileNumber = useMemo(() => {
    return <BillingProfileNumberFromId id={invoiceGroupId} txaId={transactionAccountId || ''} loaderWidth="17%" />;
  }, [invoiceGroupId, transactionAccountId]);

  const renderLabel = (label: string) => {
    return <LabelWrapper>{label}</LabelWrapper>;
  };

  return (
    <Box padding="space.300" xcss={boxStyles} testId="billing-profile-details-section">
      <Stack space="space.300">
        <Flex direction="column">
          {renderLabel('Billing profile number')}
          <CopyableText>
            <Text weight="semibold" size="large">
              {billingProfileNumber}
            </Text>
          </CopyableText>
        </Flex>
        <Inline space="space.600" rowSpace="space.300" xcss={billingProfileStyles} shouldWrap>
          <Flex direction="column">
            {renderLabel('Sold-to address')}
            {/* Not sending TXA ID so that the link is not created as per the design. */}
            <Text>{getSoldToAddress(invoice, '', false, true)}</Text>
          </Flex>
          <Flex direction="column">
            {renderLabel('Payment method')}
            <Text>
              {getPaymentMethodSection({
                invoice,
                hasPayByStatement,
                termPaymentType: onTermPaymentMethod,
                modifiedInvoice: invoice,
                termPaymentStatus,
                isPaypalEnabled,
              }) || <NoData />}{' '}
            </Text>
            <Flex gap={'space.100'} xcss={currencyBoxStyles} alignItems="center">
              {renderLabel('Currency')}
              <CurrencyCodeText>{currencyCode || <NoData testId="currency-no-data" />}</CurrencyCodeText>
            </Flex>
          </Flex>
          {invoiceGroupId && transactionAccountId && (
            <Flex direction="column">
              <ContactsTooltip type={ContactsTooltipType.CUSTOMER} iconSize="small">
                {renderLabel('Customer contacts')}
              </ContactsTooltip>
              <EndCustomerContacts
                invoiceGroupId={invoiceGroupId}
                transactionAccountId={transactionAccountId}
                timestampComparator={compareContactTimestampWithInvoiceTimestamp(invoice?.createdTimestamp)}
                version={2}
              />
            </Flex>
          )}
        </Inline>
      </Stack>
      <Inline xcss={infoStyles} alignBlock="center">
        <EditorInfoIcon label="info-icon" primaryColor={token('color.icon.discovery')} />
        <Inline alignBlock="center" space="space.050">
          <Text color="color.text.subtlest">
            This information was captured at invoice generation. Check the billing profile
          </Text>
          {billingProfileName}
          <Text color="color.text.subtlest">for up-to-date info.</Text>
        </Inline>
      </Inline>
    </Box>
  );
};
